/**
 * Template editor "Send test email" (ENG-3764 D24): render the template with sample values and call the
 * `email-sender` bot's `send-test-email` action, which sends to the requester's own email with a `[TEST]`
 * subject and creates no Task. Kept apart from `notification-email-task-builder` (which the rules-engine bots
 * bundle) so the bots never reach this UI-only path.
 */
import { ContentType, type MedplumClient } from '@medplum/core';

import type { NotificationTemplateConfig } from '../notification-template.types';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';
import {
  loadNotificationTemplateConfig,
  renderNotificationTemplate,
  validateTemplateStatusForMode,
} from './notification-email-task-builder';

export const NOTIFICATION_EMAIL_BOT = {
  system: 'https://unify.health/bots',
  value: 'email-sender',
} as const;

export interface SendTestEmailResult {
  ok: boolean;
  /** The Communication the email-sender bot recorded for the send. */
  communicationId?: string;
  error?: string;
  suppressionReason?: string;
}

// ---------------------------------------------------------------------------
// 4. High-level: send test email (email-sender `send-test-email`, ENG-3764 D24)
// ---------------------------------------------------------------------------

export interface SendTestEmailInput {
  templateId?: string;
  templateConfig?: NotificationTemplateConfig;
  /** Must be the signed-in user's own email; the bot rejects any other recipient. */
  recipientEmail: string;
  sampleContext?: Record<string, string>;
}

export async function sendTestEmail(
  medplum: MedplumClient,
  input: SendTestEmailInput,
): Promise<SendTestEmailResult> {
  let config: NotificationTemplateConfig;

  if (input.templateConfig) {
    config = input.templateConfig;
  } else if (input.templateId) {
    const loaded = await loadNotificationTemplateConfig(medplum, input.templateId);
    if (!loaded) {
      return { ok: false, error: 'Template not found', suppressionReason: 'missing-template' };
    }
    const statusCheck = validateTemplateStatusForMode(loaded, 'test');
    if (!statusCheck.valid) {
      return { ok: false, error: `Template cannot be used: ${statusCheck.reason}`, suppressionReason: statusCheck.reason };
    }
    config = loaded.config;
  } else {
    return { ok: false, error: 'Either templateId or templateConfig is required' };
  }

  const renderResult = await renderNotificationTemplate(medplum, config, {
    mode: 'test',
    sampleContext: input.sampleContext,
  });

  if (renderResult.unresolvedRequired.length > 0) {
    return {
      ok: false,
      error: `Missing required variables: ${renderResult.unresolvedRequired.join(', ')}`,
      suppressionReason: 'missing-required-variable',
    };
  }

  // The template's sender, as a real send uses it (blank means the default From).
  const from = config.from?.email?.trim();
  const fromName = config.from?.name?.trim();

  try {
    const result = (await medplum.executeBot(
      NOTIFICATION_EMAIL_BOT,
      {
        action: 'send-test-email',
        kind: 'template',
        to: input.recipientEmail,
        subject: renderResult.subject,
        body: renderResult.htmlBody,
        ...(renderResult.textBody ? { textBody: renderResult.textBody } : {}),
        ...(from ? { from, ...(fromName ? { fromName } : {}) } : {}),
      },
      ContentType.JSON,
    )) as { ok?: boolean; communicationId?: string; message?: string } | undefined;
    if (result?.ok === false) {
      return { ok: false, error: result.message || 'Test email was not sent' };
    }
    return { ok: true, communicationId: result?.communicationId };
  } catch (err) {
    return { ok: false, error: getUserFacingErrorMessage(err, 'Failed to send test email') };
  }
}
